/**
 * `<caos-tag>` — a free-form operational label, distinct from lifecycle state.
 * (Catalog: Status & feedback › Tag — a free-form operational label, distinct from lifecycle state.)
 *
 * A neutral chip carrying an author-supplied label (the default slot). Optional
 * `removable` shows a dismiss control that fires a cancelable `caos-tag-remove`
 * event. All colour/space/radius reads `--caos-*` tokens; `::part(tag)` and
 * `::part(remove)` are the styling seams.
 *
 * CAOS-597 — two states the incumbent's pill could show and ours could not:
 *
 *   AN AVATAR. Whatever is placed with `slot="avatar"` — a person's picture, or an icon standing for
 *   what the label is — sits ahead of the label, drawn at the label's own height and rounded.
 *   Nothing is drawn, and no gap is kept, when nothing is placed there.
 *
 *   AN ERROR. `error` marks the label as wrong — a value that no longer resolves, an address that
 *   bounced — by turning the edge and the lettering to the danger colour and putting a warning mark
 *   where the avatar would be. The mark is not the only signal: the tag also carries the words
 *   "Error:" ahead of its label for anybody who cannot see the colour or the mark.
 *
 * `::part(avatar)` and `::part(error)` are the seams for those two. Held in a pill container, the tag
 * itself takes focus (see `pill-container.ts`), so it draws its own focus ring when it has it — and its
 * dismiss control leaves the tab order, because there Delete is how the keyboard removes a pill and the
 * row has to stay one tab stop. Placed anywhere else, the dismiss control is an ordinary button in it.
 */
import { CaosElement, define, makeSheet } from '../base-element.js';
import { makeIcon } from './icons.js';

const sheet = makeSheet(`
  :host { display: inline-flex; vertical-align: middle; }
  :host(:focus) { outline: none; }

  .tag {
    display: inline-flex;
    align-items: center;
    gap: var(--caos-space-2);
    padding: 4px 10px;
    border-radius: var(--caos-radius); /* SQUARE 2px — Dragon chips are not pills */
    border: 1px solid var(--caos-color-border);
    background: var(--caos-color-surface-2);
    color: var(--caos-color-text-muted);
    font-family: var(--caos-font-mono);
    font-size: 10px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1.5;
    white-space: nowrap;
  }
  :host(:focus-visible) .tag { box-shadow: var(--caos-focus-ring); }

  .avatar { display: none; }
  .avatar.has-content { display: inline-flex; }
  ::slotted([slot="avatar"]) {
    display: block;
    flex: none;
    width: 15px;
    height: 15px;
    border-radius: 999px;
    object-fit: cover;
    overflow: hidden;
  }
  .avatar.has-content { margin-inline-start: -4px; }

  .error {
    display: none;
    flex: none;
    width: 13px;
    height: 13px;
    margin-inline-start: -2px;
  }
  .error svg { display: block; width: 100%; height: 100%; }
  .error-words {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
    border: 0;
  }
  :host([error]) .tag {
    border-color: var(--caos-color-danger);
    color: var(--caos-color-danger);
  }
  :host([error]) .error { display: inline-flex; }
  :host([error]) .avatar.has-content { display: none; }

  .remove {
    display: none;
    appearance: none;
    border: none;
    background: transparent;
    color: var(--caos-color-text-muted);
    font: inherit;
    font-size: 12px;
    line-height: 1;
    padding: 0;
    margin: 0;
    margin-inline-end: -2px;
    cursor: pointer;
    border-radius: var(--caos-radius-sm);
  }
  :host([removable]) .remove { display: inline-flex; }
  :host([error]) .remove { color: var(--caos-color-danger); }
  .remove:hover { color: var(--caos-color-danger); }
  .remove:focus-visible { outline: none; box-shadow: var(--caos-focus-ring); }
`);

export class CaosTag extends CaosElement {
  static get observedAttributes(): string[] {
    return ['error'];
  }

  private readonly errorWords: HTMLSpanElement;
  private readonly removeButton: HTMLButtonElement;

  constructor() {
    super(sheet);
    const tag = document.createElement('span');
    tag.className = 'tag';
    tag.setAttribute('part', 'tag');

    const avatar = document.createElement('span');
    avatar.className = 'avatar';
    avatar.setAttribute('part', 'avatar');
    const avatarSlot = document.createElement('slot');
    avatarSlot.name = 'avatar';
    avatarSlot.addEventListener('slotchange', () => {
      avatar.classList.toggle('has-content', avatarSlot.assignedNodes().length > 0);
    });
    avatar.append(avatarSlot);

    const error = document.createElement('span');
    error.className = 'error';
    error.setAttribute('part', 'error');
    error.setAttribute('aria-hidden', 'true');
    error.append(makeIcon('warning'));

    this.errorWords = document.createElement('span');
    this.errorWords.className = 'error-words';

    const remove = document.createElement('button');
    remove.className = 'remove';
    remove.type = 'button';
    remove.setAttribute('part', 'remove');
    remove.setAttribute('aria-label', 'Remove');
    remove.textContent = '×';
    remove.addEventListener('click', () => this.requestRemove());
    this.removeButton = remove;

    tag.append(avatar, error, this.errorWords, document.createElement('slot'), remove);
    this.root.append(tag);
  }

  connectedCallback(): void {
    this.syncError();
    // Moving a tag disconnects and reconnects it, so this is read again wherever it lands.
    this.removeButton.setAttribute('tabindex', this.parentElement?.localName === 'caos-pill-container' ? '-1' : '0');
  }

  attributeChangedCallback(): void {
    this.syncError();
  }

  /**
   * Report that somebody asked to take this tag off — the dismiss control, or the Delete key inside a
   * pill container. The tag never removes itself; the host does, once the record agrees. Returns
   * whether the event went unrefused.
   */
  requestRemove(): boolean {
    return this.dispatchEvent(new CustomEvent('caos-tag-remove', { bubbles: true, composed: true, cancelable: true }));
  }

  private syncError(): void {
    this.errorWords.textContent = this.hasAttribute('error') ? 'Error: ' : '';
  }
}

define('caos-tag', CaosTag);

declare global {
  interface HTMLElementTagNameMap {
    'caos-tag': CaosTag;
  }
}
